/**
 * Every word a client reads, in Castilian.
 *
 * This file is the floor catalogue and therefore the definition of what a
 * message id *is*: `MessageId` is `keyof typeof ES`, so a screen cannot ask for
 * an id that is not here, and a second language cannot ship with a hole in it.
 * Both are compile errors, which is what `client-language`'s "A new screen
 * cannot ship untranslated" asks for — the build fails rather than a client
 * meeting a gap.
 *
 * Three rules the wording follows, and they are requirements rather than taste:
 *
 *   * **No name is a literal here.** The coach, the instance and the platform
 *     are data (`coach_instance`) or configuration (`VITE_PLATFORM_NAME`), and
 *     `pnpm check:names` fails the build on one. Where a name belongs in a
 *     sentence it is a `{coach}` placeholder.
 *   * **Gender-neutral by construction** (`docs/decisions/spanish-jurisdiction.md`,
 *     rule 2). The product acquires no gender field, and the coach's own gender
 *     is not ours to assume in a sentence — hence "quien te entrena" rather than
 *     an inflected noun, and "en cuanto lo haga" rather than "cuando ella lo
 *     haga".
 *   * **Nothing here implies a medical qualification.** The coach is a personal
 *     trainer who is also a medical student; no string may call her a doctor,
 *     name her a physician, or describe what she writes as medical advice.
 *
 * **Three of these are also the server's words.** The welcome screen shows what
 * `redeem-invite` returned, so `REDEEM_COPY` in
 * `supabase/functions/redeem-invite/index.ts` says the same three sentences —
 * `client.welcome.unusable`, `client.welcome.expiredInvite` and
 * `client.welcome.signInFirst`. `tests/rls/invite.test.ts` asserts that the two
 * sides agree, because *which* failures are indistinguishable from each other is
 * a security property and a screen that says one thing while the server says
 * another is how that property gets lost. An Edge Function cannot import this
 * file — it is Deno, and this is bundled for a browser — so the two are kept in
 * step by that test rather than by a shared module.
 *
 * **Known gap, named rather than discovered.** `NotFound` and `Loading` live in
 * `src/ui` and `src/app/admin`, are shared with the coach's side, and are still
 * English. A client reaches both. They are outside the three directories this
 * seam covers, and moving them means putting the admin's copy through a
 * catalogue it has no other reason to use; `client-daily-loop` builds the client
 * shell and should take them with it.
 */

export const ES = {
  /* ---------------------------------------------------------------- shell --- */
  'app.loading': 'Cargando…',

  /* ----------------------------------------------------------------- home --- */
  /** The coach and her role line, as the instance row spells them. */
  'client.home.coachLine': '{coach}, {role}.',
  'client.home.signedIn': 'Has iniciado sesión.',
  'client.home.signIn':
    'Abre el enlace del correo de invitación para entrar. No hay nada que registrar.',
  'client.home.goToWeek': 'Ir a esta semana',

  /* ----------------------------------------------------------------- week --- */
  'client.today.signOut': 'Cerrar sesión',
  'client.today.greeting': 'Hola, {name}',
  'client.today.title': 'Esta semana',
  'client.today.nothingTitle': 'Aún no hay nada publicado',
  'client.today.nothingBody':
    '{coach} todavía no ha publicado ninguna semana para ti. Aparecerá aquí en cuanto lo haga.',
  'client.today.week': 'Semana {number}',
  'client.today.published': 'Publicada',
  'client.today.from': 'desde el {date}',
  'client.today.noSessions': 'Esta semana todavía no tiene sesiones.',
  'client.today.updated': 'Actualizado el {moment}',

  /** How many of how many are marked. A count, never a percentage or a grade
   *  (`client-tone`, "Progress is shown without being scored"). */
  'client.today.progress': '{done} de {total} hechos',

  /* ---------------------------------------------------------------- today --- */
  /** The line above the greeting: which day the client is actually having. */
  'client.today.dateLine': '{weekday}, {date}',
  'client.today.todayEyebrow': 'Hoy',
  'client.today.fromCoach': 'De {coach}',
  /** Spanish has no "0 ejercicio", and one exercise is not "1 ejercicios". */
  'client.today.exercisesOne': '1 ejercicio',
  'client.today.exercisesMany': '{count} ejercicios',
  'client.today.start': 'Empezar la sesión',
  /** The same control, for a session already part-way through. */
  'client.today.continue': 'Seguir con la sesión',
  /**
   * A day with nothing planned is the most ordinary day there is.
   *
   * Nothing in these two sentences suggests the client has done anything wrong,
   * asks them to account for the day, or calls it a rest day they have to earn
   * (`client-tone`, "A session not done is stated neutrally").
   */
  'client.today.noSessionTitle': 'Hoy no hay sesión',
  'client.today.noSessionBody': 'Nada programado para hoy.',
  'client.today.nextIs': 'La próxima: {title}, el {weekday}.',
  'client.today.nextNone': 'Por ahora no hay ninguna sesión más programada.',
  /**
   * Done, and nothing else. No total, no run of days, no comparison with any
   * earlier session (`client-tone`, "Completing a session is acknowledged once
   * and modestly").
   */
  'client.today.doneEyebrow': 'Hecha',
  'client.today.doneAt': 'La terminaste a las {time}.',
  'client.today.view': 'Ver la sesión',

  /* -------------------------------------------------------------- session --- */
  'client.session.back': 'Volver a hoy',
  'client.session.noExercises': 'No hay ejercicios en esta sesión.',
  'client.session.howTo': 'Cómo se hace',
  /**
   * Whose words these are. `exercise-catalogue` requires that a client can tell
   * a stock description apart from what her coach wrote, and for a coach who is
   * a medical student that is not a presentational concern.
   */
  'client.session.howToDisclaimer':
    'Descripción general de este movimiento. No la ha escrito quien te entrena.',
  /** Marking is optional, so the label says what it does and asks nothing. */
  'client.session.markLabel': 'Marcar {name} como hecho',
  'client.session.unmarkLabel': 'Quitar la marca de {name}',
  'client.session.finish': 'Terminar la sesión',
  'client.session.doneBadge': 'Hecha',
  'client.session.completedAt': 'La terminaste a las {time}.',
  /**
   * Her coach's own recording, attributed to her wherever it appears — the
   * client must always be able to tell her words from the catalogue's.
   */
  'client.session.video': 'Vídeo de {coach}',
  'client.session.videoOpen': 'Ver el vídeo',
  /**
   * Said out loud rather than hidden behind a control that does nothing. There
   * is no storage bucket for a coach's video in any migration, so there is no
   * file to play; a play button that silently failed would be a lie told by a
   * screen rather than a limitation stated by one.
   */
  'client.session.videoMissing': 'Esta demostración todavía no incluye el archivo del vídeo.',

  /* ------------------------------------------------------------- check-in --- */
  'client.checkIn.back': 'Volver a la sesión',
  'client.checkIn.title': '¿Qué tal ha ido?',
  'client.checkIn.intro': 'Dos toques y listo.',
  'client.checkIn.effort': 'Esfuerzo',
  'client.checkIn.effortHint': 'Del 1, muy suave, al 10, al límite.',
  'client.checkIn.effortOption': 'Esfuerzo {value} de 10',
  'client.checkIn.feeling': '¿Cómo te has sentido?',
  'client.checkIn.feelingGreat': 'Genial',
  'client.checkIn.feelingGood': 'Bien',
  'client.checkIn.feelingOk': 'Normal',
  'client.checkIn.feelingTough': 'Duro',
  'client.checkIn.feelingRough': 'Muy duro',
  /**
   * Asked **only** of a client who opted in, and answerable only yes or no.
   *
   * There is no body-area picker and no pain-description field anywhere in this
   * catalogue, and there must never be one: `mvp-discovery` → `client-app`
   * forbids asking where it hurt or for any description of pain, and the person
   * reading the answer is a personal trainer who is also a medical student —
   * not a physician, and not someone who may take a symptom report.
   */
  'client.checkIn.hurt': '¿Te ha molestado algo?',
  'client.checkIn.hurtNo': 'No',
  'client.checkIn.hurtYes': 'Sí',
  /**
   * The safety message, shown the moment the answer is yes.
   *
   * "Tu médico" is the client's **own** doctor, which is the one medical
   * reference this product is allowed to make. Nothing here suggests that
   * whoever trains them can assess it.
   */
  'client.checkIn.hurtSafety':
    'No sigas forzando. Díselo a {coach} antes de la próxima sesión y, si el dolor es fuerte, no se pasa o te preocupa, consúltalo con tu médico.',
  /** Always offered, opted in or not, so nobody is left without a way to say something. */
  'client.checkIn.note': '¿Algo más?',
  'client.checkIn.notePlaceholder': 'Opcional',
  'client.checkIn.noteHint':
    'Escríbelo corto. Para cualquier cosa sobre dolor o salud, díselo directamente a {coach}.',
  /**
   * The safety path's other half: the offer that makes "díselo a {coach}" true.
   *
   * It opens the thread carrying a reference to the session, and nothing else.
   * It prefills no text, asks no question and sends nothing — `mvp-discovery` →
   * `progress-and-safety` requires "an empty message" and "nothing is sent until
   * she writes and sends".
   *
   * It is offered only to a client who opted in to stored messages. One who did
   * not still reads the safety message above, which already names both the
   * person who trains them and their own doctor, so the route out is never lost
   * to a privacy choice.
   */
  'client.checkIn.hurtMessage': 'Contárselo a {coach}',
  'client.checkIn.submit': 'Terminar la sesión',
  'client.checkIn.missing': 'Marca el esfuerzo y cómo te has sentido.',
  'client.checkIn.doneTitle': 'Sesión terminada',
  'client.checkIn.doneBody': '{coach} verá tu registro.',
  'client.checkIn.doneBack': 'Volver a hoy',

  /* ------------------------------------------------------------- la semana --- */
  /**
   * The week: what is asked of the client, what is behind them, and what is
   * being prepared.
   *
   * **Four words carry the whole tone of this screen**, and they were chosen
   * against a list rather than for their sound. `client-tone` forbids "missed",
   * "failed", "skipped", "lost", "broken" and every synonym that assigns fault,
   * so a session whose day has passed is *sin hacer* — literally "not done", a
   * description of the world with nobody in it. It is not *perdida*, not
   * *fallada*, not *saltada*, and there is no word anywhere on this screen for
   * how long it has been.
   *
   * There is also **no summary of a week**: no total, no "2 de 3", no
   * percentage and no comparison with any other week. A count is allowed inside
   * a single session (`client.today.progress`) because it tells a client where
   * they are in the thing they are doing. A count of a *week* is a mark out of
   * three, and a mark is what this product does not give.
   */
  'client.week.back': 'Volver a hoy',
  /** The way in, from Today. */
  'client.week.seeWeek': 'Ver la semana entera',
  'client.week.range': 'Del {from} al {to}',
  'client.week.previous': 'Semana anterior',
  'client.week.next': 'Semana siguiente',
  /** The four states a session can be in, and there is no fifth. */
  'client.week.today': 'Hoy',
  'client.week.done': 'Hecha',
  'client.week.notDone': 'Sin hacer',
  'client.week.ahead': 'Por hacer',
  /**
   * The offer on a session that was not done — an ordinary verb, in the
   * infinitive, with no adverb of time and no apology attached. It is the same
   * invitation every other session on the screen carries; it is written down
   * only because a session in the past would otherwise look closed.
   */
  'client.week.doIt': 'Hacerla',
  /**
   * A day the coach put nothing on.
   *
   * It is a part of the plan and it is named as one. Not "no hay nada", which
   * reads as data that failed to load, and not a rest the client has to have
   * earned (`client-tone`, and the same rule that shaped
   * `client.today.noSessionTitle`).
   */
  'client.week.rest': 'Descanso',
  /** Where a session opened from the week goes back to. */
  'client.week.backToWeek': 'Volver a la semana',
  /**
   * The truth about this screen, permanently under the week.
   *
   * Whether a session is done lives in one browser's storage and nowhere else
   * (`src/app/client/progress.ts`). A week view is where a client goes
   * specifically to check what they have done, so a history that quietly empties
   * when they change phone is worse than a tick that quietly does nothing — and
   * `client-coach-presence` set the rule that the screen says so rather than
   * letting it be discovered.
   *
   * It is one quiet sentence in the smallest weight, at the bottom, with no
   * alarm colour and nothing asked of the client. It is not a warning; it is a
   * fact about this version.
   */
  'client.week.onThisDevice':
    'Lo que marcas como hecho se guarda solo en este dispositivo. Si entras desde otro, no aparecerá.',
  /**
   * A week the coach has not published.
   *
   * The client is never described as blocked, unauthorised or lacking access —
   * this is not a permissions message and must not read as one. It says what is
   * true: the person coaching them is still writing it. Nothing of its content
   * is shown, because the product does not have it: a draft week is not in the
   * client's query results at all.
   */
  'client.week.notReadyTitle': 'Todavía no está lista',
  'client.week.notReadyBody': '{coach} la está preparando. Aparecerá aquí en cuanto la publique.',

  /* --------------------------------------------------- the coach, in person --- */
  /**
   * The three ways to reach the person doing the coaching, from Today.
   *
   * **This is not a tab bar and it is deliberately not one.** The hi-fi has
   * three tabs, `mvp-discovery` → `client-app` describes five, and that
   * disagreement is an information-architecture decision belonging to the owner
   * rather than to this change — whose own spec says nothing about navigation at
   * all. So the three destinations this slice builds are reachable from Today,
   * where a client already is, and the question stays open instead of being
   * answered by whoever implemented it.
   */
  'client.presence.section': 'Con {coach}',
  'client.presence.notes': 'Notas',
  'client.presence.notesMeta': 'Lo que {coach} te ha escrito',
  'client.presence.videos': 'Vídeos',
  'client.presence.videosMeta': 'Sus ejercicios, grabados',
  'client.presence.messages': 'Mensajes',
  'client.presence.messagesMeta': 'Escríbele si algo no encaja',
  /** Never a dot alone: a state carried only by a colour is a state some
   *  readers do not have. */
  'client.presence.unread': 'Sin leer',

  /* ---------------------------------------------------------------- notes --- */
  'client.notes.back': 'Volver a hoy',
  'client.notes.title': 'Notas',
  'client.notes.emptyTitle': 'Todavía no hay notas',
  /** Nothing is asked of the client here, and nothing is implied to be missing
   *  on their side. */
  'client.notes.emptyBody':
    '{coach} aún no te ha escrito nada. Aparecerá aquí en cuanto lo haga y no tienes que hacer nada.',
  /**
   * The three kinds, and there is no fourth (`mvp-discovery` →
   * `recommendations`, "There SHALL be no medical or clinical type").
   */
  'client.notes.kindTraining': 'Entrenamiento',
  'client.notes.kindNutrition': 'Alimentación',
  'client.notes.kindRecovery': 'Descanso',
  'client.notes.readerBack': 'Volver a las notas',
  'client.notes.published': 'Publicada el {date}',
  /** Whose words these are, on every note, from the instance row. */
  'client.notes.from': 'De {coach}, {role}',
  /**
   * The standing boundary line, on every note, never editable and never absent.
   *
   * It is built from the coach's configured name and role line rather than
   * written as fixed text (`recommendations`, "Authorship, date and boundary
   * line"), and "tu médico" is the client's **own** doctor — the one medical
   * reference this product is allowed to make.
   */
  'client.notes.boundary':
    'Consejo de entrenamiento de {coach}, {role}. No es consejo médico. Si te encuentras mal, te duele algo o no sabes si esto es adecuado para ti, consúltalo con tu médico.',

  /* --------------------------------------------------------------- vídeos --- */
  'client.videos.back': 'Volver a hoy',
  'client.videos.title': 'Vídeos',
  'client.videos.intro': 'Los ejercicios que {coach} ha grabado y que están en tus sesiones.',
  'client.videos.emptyTitle': 'Todavía no hay vídeos',
  /** Said plainly. Never an empty grid, never a spinner that does not resolve. */
  'client.videos.emptyBody':
    '{coach} aún no ha grabado ninguno. Cuando lo haga, los verás aquí y también dentro de la sesión.',

  /* ------------------------------------------------------------- mensajes --- */
  'client.messages.back': 'Volver a hoy',
  'client.messages.title': 'Mensajes',
  /**
   * What the thread is, said inside the thread.
   *
   * She is described by the role the instance row gives her and never as a
   * doctor: she is a personal trainer who is also a medical student, and this is
   * the screen where a client is most likely to ask her something medical.
   */
  'client.messages.notice':
    '{coach} es {role} y no puede decirte qué te pasa; para eso está tu médico. Esto no es un historial médico.',
  /**
   * Not an emergency channel, stated in the thread itself.
   *
   * The number is not named. There is no emergency-number setting on
   * `coach_instance` and this change adds no migration, so naming one would mean
   * hard-coding a number that is right in Spain and wrong for a client on
   * holiday. "El de tu zona" is correct everywhere and is what the spec asks for.
   */
  'client.messages.emergency':
    'Esto no se vigila. Si es una urgencia, llama al número de emergencias de tu zona.',
  'client.messages.emptyThread': 'Aquí todavía no hay nada. Escribe cuando quieras.',
  'client.messages.fromCoach': 'De {coach}',
  'client.messages.fromMe': 'Tú',
  'client.messages.unread': 'Sin leer',
  'client.messages.composerLabel': 'Escribe un mensaje',
  'client.messages.composerPlaceholder': 'Lo que quieras contarle',
  'client.messages.send': 'Enviar',
  /**
   * The truth about this screen, permanently under the composer.
   *
   * There is no `message` table and no coach's side. What a client writes stays
   * in this browser. A composer that accepted a message and implied it had been
   * delivered would be the worst thing in this product, so it says so instead.
   */
  'client.messages.notDelivered':
    'En esta versión lo que escribes se queda en este dispositivo: no le llega a {coach} ni se guarda en ningún sitio.',
  /** The reference a message carries when it was started from a session. It is
   *  a reference, not a description: nothing asks what happened. */
  'client.messages.re': 'Sobre: {title}',
  'client.messages.reRemove': 'Quitar la referencia a la sesión',
  'client.messages.offTitle': 'Los mensajes están desactivados',
  'client.messages.offBody':
    'No has activado que se guarden tus mensajes, así que aquí no hay ningún hilo y no se guarda nada.',
  /** The choice, offered once, here, and repeated nowhere else in the product. */
  'client.messages.offHow': 'Se activa desde tu perfil, en la parte de privacidad.',

  /* -------------------------------------------------------------- welcome --- */
  'client.welcome.working': 'Iniciando sesión…',
  /**
   * An invitation already used by somebody else and one whose address does not
   * match the session say **exactly this same sentence**, deliberately: a
   * different message would confirm which client an invitation belongs to
   * (`access-and-consent`, "no hint that the content exists").
   */
  'client.welcome.unusable': 'Esta invitación no se puede usar. Pide una nueva a quien te entrena.',
  /** An invitation past its fourteen days. "This is old" reveals nothing. */
  'client.welcome.expiredInvite': 'Esta invitación ha caducado. Pide una nueva a quien te entrena.',
  /** The one-time sign-in link *inside* the invitation, which is a different thing. */
  'client.welcome.expiredLink': 'Este enlace ha caducado.',
  'client.welcome.expiredLinkBody':
    'Pide a {coach} que te envíe uno nuevo y ábrelo en el dispositivo en el que quieras seguir con la sesión iniciada.',
  'client.welcome.expiredLinkAction': 'Quiero un enlace nuevo',
  'client.welcome.noInvitation': 'Abre el enlace del correo de invitación para entrar.',
  /** Shown when the call is made with no session at all. */
  'client.welcome.signInFirst': 'Inicia sesión primero.',

  /* --------------------------------------------------------- el acuerdo --- */
  /**
   * The five sentences a client is asked to agree to, and the only place they
   * are written.
   *
   * **These five ids are the carve-out.** `legal-review-at-the-gate`'s
   * structural list requires that a consent record carry "the exact text the
   * client was shown, the language it was rendered in, the version of the source
   * text". So the sentence lives here, identified by an id, stamped with a
   * version in `src/shared/consent.ts`, and rendered **verbatim** into the
   * record at the moment the client ticks it — with `{coach}` and `{role}`
   * already resolved, because she can change her role line afterwards and the
   * record must still say what the client actually read.
   *
   * Rewording any of them is therefore not a copy edit: it is a new source
   * version, and `CONSENT_TEXT_VERSION` moves with it. Earlier records keep the
   * earlier words.
   *
   * Two of them are **required** and three are optional. The required pair is
   * the acknowledgement this change's spec names and the age confirmation the
   * carve-out names; neither is optional and neither is ticked by default.
   */
  'consent.ack.text':
    'Entiendo que {coach} es {role}, que lo que trabajamos aquí es entrenamiento y no un tratamiento médico, y que esta aplicación no está vigilada: si tengo una urgencia, llamo al número de emergencias de mi zona.',
  /**
   * Age, as the carve-out shapes it: one tick, and **no date of birth anywhere**.
   * The product stores that the confirmation was made and when; it never stores
   * how old anybody is, because it does not need to and cannot un-store it.
   */
  'consent.age.text': 'Confirmo que tengo 18 años o más.',
  'consent.metrics.text':
    'Quiero apuntar mis medidas (el peso y algunos contornos) y que se guarden para poder verlas con el tiempo. Puedo desactivarlo cuando quiera y se borrarán.',
  'consent.messaging.text':
    'Quiero poder escribirle a {coach} desde aquí y que se guarden los mensajes. Puedo desactivarlo cuando quiera y se borrarán.',
  /** Yes or no, and never where or how much. See `client.checkIn.hurt`. */
  'consent.hurt.text':
    'Quiero que, al terminar una sesión, se me pregunte si me ha molestado algo. Solo sí o no: nunca dónde ni cuánto.',

  /** The short name of each choice, for the rows on the profile screen. */
  'consent.ack.name': 'Lo que acepté al entrar',
  'consent.age.name': 'Mayor de edad',
  'consent.metrics.name': 'Mis medidas',
  'consent.messaging.name': 'Mensajes',
  'consent.hurt.name': 'La pregunta de después de entrenar',

  /* -------------------------------------------------------- la entrada --- */
  /**
   * The way in: what this is, what it keeps, who sees it, and how to get it out.
   *
   * Written last on purpose (`design.md` D4). It is the one screen nobody wants
   * to read, so it is short, it is in plain words, and it says the unflattering
   * parts — that nothing is watched, and that in this version a lot of it stays
   * on the phone — instead of burying them.
   */
  'client.start.title': 'Antes de empezar',
  'client.start.intro':
    'Esto es la aplicación de {coach}. Aquí verás tus sesiones, lo que te escriba y, si quieres, tus medidas.',
  'client.start.storedTitle': 'Qué se guarda',
  'client.start.storedBody':
    'Tu nombre y tu correo, el plan que {coach} prepara para ti y lo que marques como hecho. Lo demás lo eliges tú más abajo. Nunca se te pregunta por lesiones, enfermedades ni medicación.',
  'client.start.whoTitle': 'Quién lo ve',
  'client.start.whoBody':
    '{coach} y nadie más. Ningún otro cliente puede ver nada tuyo, y tú no puedes ver nada suyo.',
  'client.start.rightsTitle': 'Cómo sacarlo o borrarlo',
  'client.start.rightsBody':
    'Desde tu perfil puedes pedir una copia de todo lo que hay sobre ti, o pedir que se borre tu cuenta. No hace falta que des ninguna explicación.',
  'client.start.requiredTitle': 'Lo que hace falta para seguir',
  'client.start.optionalTitle': 'Lo que puedes elegir',
  'client.start.optionalIntro':
    'Nada de esto hace falta para usar la aplicación. Lo que no actives, simplemente no aparece, y no se te vuelve a preguntar.',
  'client.start.continue': 'Entrar',
  /** Names the tick that is missing, rather than saying "faltan campos". */
  'client.start.missingAck': 'Para seguir tienes que marcar: {name}.',
  /**
   * The truth about the way in, on the way in.
   *
   * There is no `client_update_self` policy (`00005_policies.sql`), so a client
   * cannot write their own consent columns, and this change adds no migration.
   * What is ticked here is kept by this browser. Said here rather than
   * discovered on the second phone.
   */
  'client.start.onThisDevice':
    'En esta versión lo que marcas aquí se guarda solo en este dispositivo. Si entras desde otro, se te volverá a preguntar.',

  /* ----------------------------------------------------------- tu perfil --- */
  'client.me.link': 'Tu perfil y tu privacidad',
  'client.me.back': 'Volver a hoy',
  'client.me.title': 'Tu perfil',
  /**
   * The identity block, and everything it deliberately does not have.
   *
   * The hi-fi draws a stats grid here with body fat, resting heart rate, age and
   * gender in it. None of the four is built, and two of them are refusals rather
   * than omissions — counsel's body-metrics ruling names body fat and a computed
   * index outright, and resting heart rate is physiological data. Age exists as
   * one tick and no date of birth; gender was deliberately never acquired,
   * because the Castilian copy was written around it instead.
   */
  'client.me.since': 'Aquí desde el {date}',
  'client.me.coachTitle': 'Quien te entrena',
  'client.me.coachLine': '{coach}, {role}.',
  'client.me.coachWrite': 'Escríbele',
  /** The route out that survives every privacy choice (`design.md` D5). */
  'client.me.coachNoMessages':
    'No tienes activados los mensajes, así que cuéntaselo a {coach} como lo hagáis normalmente. Si te duele algo, no lo dejes pasar y consúltalo con tu médico.',

  /**
   * The boundary, on the profile screen, without opening anything.
   *
   * She is named by the role the instance row gives her — a personal trainer who
   * is also a medical student — and never as a doctor. "Tu médico" is the
   * client's own.
   */
  'client.me.boundaryTitle': 'Qué es esto y qué no es',
  'client.me.boundaryBody':
    'Esto es entrenamiento de {coach}, {role}. No es consejo médico, ni un diagnóstico, ni un tratamiento, y no es un historial médico. Nadie vigila esta aplicación: si es una urgencia, llama al número de emergencias de tu zona; si te encuentras mal o te duele algo, consúltalo con tu médico.',

  'client.me.privacyTitle': 'Privacidad',
  'client.me.privacyIntro':
    'Puedes cambiar cualquiera de estas opciones cuando quieras. Lo que desactives se borra.',
  'client.me.consentOn': 'Activado',
  'client.me.consentOff': 'Desactivado',
  'client.me.consentSince': 'Desde el {date}',
  'client.me.consentToggle': 'Cambiar: {name}',
  'client.me.ackGiven': 'Lo aceptaste el {date}',
  'client.me.ackPending': 'Todavía no lo has aceptado.',

  /** The warning that comes **before** the confirmation, never after (D6). */
  'client.me.withdrawTitle': '¿Desactivar {name}?',
  'client.me.withdrawMetrics':
    'Se borrarán todas las medidas que has apuntado y dejarás de ver esa parte de la aplicación. No se puede deshacer: si vuelves a activarlo, empezarás de cero.',
  'client.me.withdrawMessaging':
    'Se borrarán los mensajes que has escrito y dejarás de ver el hilo. No se puede deshacer: si vuelves a activarlo, empezarás de cero.',
  'client.me.withdrawHurt':
    'Se dejará de preguntarte si te ha molestado algo al terminar una sesión.',
  'client.me.withdrawConfirm': 'Sí, desactivar',
  'client.me.withdrawCancel': 'Dejarlo como está',

  'client.me.rightsTitle': 'Tus datos',
  'client.me.exportAction': 'Pedir una copia de mis datos',
  'client.me.exportDoneTitle': 'Copia pedida',
  'client.me.exportDoneBody':
    'Recibirás todo lo que hay sobre ti — tu nombre, tu correo, tu plan, lo que has marcado y lo que hayas apuntado — en el plazo de un mes.',
  'client.me.deleteAction': 'Pedir que se borre mi cuenta',
  'client.me.deleteTitle': '¿Borrar tu cuenta?',
  /** No reason is asked for, here or anywhere (`access-and-consent`). */
  'client.me.deleteBody':
    'Se borrará todo lo que hay sobre ti: tu cuenta, tu plan, lo que has marcado y lo que hayas apuntado. No hace falta que digas por qué.',
  'client.me.deleteConfirm': 'Sí, pedir que se borre',
  'client.me.deleteCancel': 'Ahora no',
  'client.me.deleteDoneTitle': 'Borrado pedido',
  'client.me.deleteDoneBody':
    'Se borrará todo lo que hay sobre ti en el plazo de un mes. Mientras tanto puedes seguir entrando.',
  /**
   * The truth about both requests, beside them.
   *
   * There is nowhere for a request to arrive: no table, no inbox, no email, and
   * no coach's side. A data right the product offers and cannot deliver is worse
   * than one it does not offer (`design.md`, Risks), so the screen says so and
   * the gate carries it as a line that must be met before the first real client.
   */
  'client.me.requestNotDelivered':
    'En esta versión esta petición no le llega a nadie: no sale de este dispositivo. Díselo también a {coach} directamente.',

  'client.me.onThisDevice':
    'En esta versión tus opciones de privacidad y tus medidas se guardan solo en este dispositivo. Si entras desde otro, no aparecerán.',

  /* --------------------------------------------------------- tus medidas --- */
  'client.me.measurementsLink': 'Tus medidas',
  'client.measurements.back': 'Volver a tu perfil',
  'client.measurements.title': 'Tus medidas',
  'client.measurements.intro':
    'Las apuntas tú, cuando quieras. No hace falta ninguna para usar el resto de la aplicación, y {coach} no las apunta por ti.',
  'client.measurements.addTitle': 'Apuntar hoy',
  'client.measurements.addHint':
    'Rellena solo lo que quieras. Lo que dejes en blanco no se guarda.',
  'client.measurements.save': 'Guardar',
  'client.measurements.saved': 'Apuntado el {date}.',
  'client.measurements.nothingEntered': 'Escribe al menos un número para guardar.',
  'client.measurements.badNumber': 'Escribe un número.',

  /** The fixed set. Five, and there is no control anywhere to add a sixth. */
  'client.measurements.weight': 'Peso',
  'client.measurements.waist': 'Cintura',
  'client.measurements.hip': 'Cadera',
  'client.measurements.arm': 'Brazo',
  'client.measurements.thigh': 'Muslo',

  /**
   * The two neutral words, and the whole vocabulary of this screen.
   *
   * Never "progreso", never "has perdido" or "has ganado", never an arrow and
   * never a colour for direction. A weight that went up is a fact
   * (`client-tone`); a weight that went up in red against a goal line is a
   * verdict, and this product does not give verdicts about bodies.
   */
  'client.measurements.latest': 'Último',
  'client.measurements.change': 'Cambio',
  'client.measurements.onDate': 'el {date}',
  'client.measurements.oneOnly': 'Es la única que has apuntado.',
  'client.measurements.emptyTitle': 'Todavía no has apuntado nada',
  'client.measurements.emptyBody':
    'Cuando apuntes algo aparecerá aquí, con la fecha. No hay ninguna prisa.',
  /** What the drawn line is, for a reader who does not get the drawing. */
  'client.measurements.chartLabel': '{name}: cada medida que has apuntado, con su fecha.',
  'client.measurements.historyLabel': 'Todo lo que has apuntado de {name}',
  'client.measurements.offTitle': 'Las medidas están desactivadas',
  'client.measurements.offBody':
    'No has activado que se guarden tus medidas, así que aquí no hay nada y no se guarda nada.',
  'client.measurements.offHow': 'Se activa desde tu perfil, en la parte de privacidad.',
  /** Kept on the device, like the ticks and the messages. Said, not discovered. */
  'client.measurements.onThisDevice':
    'Lo que apuntas se guarda solo en este dispositivo: no le llega a {coach} ni se guarda en ningún sitio.',

  /* ------------------------------------------------------------ not found --- */
  /**
   * The single answer to every attempt to reach something that is not yours.
   * It never says what was asked for.
   */
  'client.notFound.title': 'Página no encontrada',
  'client.notFound.body': 'La página que buscas no existe, o no tienes acceso a ella.',
} as const;
